import { AppstoreOutlined, AppstoreFilled } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Divider, notification, Space, UploadFile } from 'antd';
import Button from 'app/components/Button';
import Toolbar from 'app/components/Toolbar';
import Upload from 'app/components/Upload';
import {
  useCreateInspectionDocumentMutation,
  useDeleteInspectionDocumentMutation,
  useLazyGetInspectionDocumentQuery
} from 'app/features/inspection/api';
import { useDownloadFile } from 'app/hooks/useDownloadFile';
import useUploadFile from 'app/hooks/useUploadFile';
import { AuthContext } from 'app/providers/Authentication';
import React from 'react';
import { MdOutlineViewAgenda, MdViewAgenda } from 'react-icons/md';
import GridView from './components/GridView';
import TableView from './components/TableView';

const Container = styled.div`
  background: #fff;
  padding: 24px;
`;
const StyledToolbar = styled(Toolbar)`
  > div {
    justify-content: space-between;
    width: 100%;
  }
`;
const StyledLeftToolbar = styled(Space)`
  > .ant-space-item {
    display: flex;
    align-items: center;
    .ant-divider {
      margin: 0px;
    }
    &:not(:nth-of-type(2)) {
      cursor: pointer;
    }
  }
`;
type Props = {
  inspectionId: number | string;
};
const AttachmentTab: React.FC<Props> = ({ inspectionId }) => {
  const [fileList, setFileList] = React.useState<UploadFile[]>();
  const authContext = React.useContext(AuthContext);
  const [mode, setMode] = React.useState('list');
  const [fetch, result] = useLazyGetInspectionDocumentQuery();
  const [update] = useDeleteInspectionDocumentMutation();
  const [createInspectionDocument] = useCreateInspectionDocumentMutation();
  const { download, isDownloading } = useDownloadFile();
  const { uploadFile, isLoading } = useUploadFile();

  React.useEffect(() => {
    if (inspectionId) {
      fetch({
        inspectionId: inspectionId,
        params: {
          pagination: {
            pageNo: 1,
            pageSize: 10
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId]);

  const onChange = async (pagination: { page: number; pageSize: number }) => {
    fetch({
      ...(result.originalArgs as any),
      params: {
        pagination: {
          pageNo: pagination.page as any,
          pageSize: pagination.pageSize
        }
      }
    });
  };

  const deleteDocument = async (documentId: number) => {
    await update({
      inspectionId,
      documentId
    }).unwrap();
    fetch({
      ...(result.originalArgs as any)
    });
  };

  const handleUploadFiles = async () => {
    const files: File[] = (fileList || []).map((file) => {
      const { originFileObj } = file;

      return (originFileObj || file) as File;
    });
    const document: any[] = await uploadFile(files, authContext.company.id);
    try {
      await createInspectionDocument({
        inspectionId: inspectionId as number,
        documentsIds: document.map((document) => document.id)
      }).unwrap();

      fetch({
        ...(result.originalArgs as any)
      });
      setFileList([]);
      notification.success({
        message: 'Uploaded successful',
        placement: 'bottomLeft'
      });
    } catch (err) {
      setFileList([]);
      notification.error({
        message: 'Upload was failed',
        placement: 'bottomLeft'
      });
    }
  };

  React.useEffect(() => {
    //Hooks the trigger when fileList changed;
    if (fileList && fileList.length && !isLoading) {
      handleUploadFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileList]);

  return (
    <Container>
      <StyledToolbar>
        <StyledLeftToolbar>
          {mode === 'list' ? (
            <MdViewAgenda />
          ) : (
            <MdOutlineViewAgenda
              onClick={() => {
                setMode('list');
              }}
            />
          )}
          <Divider type="vertical" orientationMargin={2}></Divider>
          {mode !== 'list' ? (
            <AppstoreFilled />
          ) : (
            <AppstoreOutlined
              onClick={() => {
                setMode('grid');
              }}
            />
          )}
        </StyledLeftToolbar>
        <Space>
          <Button
            loading={isDownloading}
            type="primary"
            ghost
            disabled={!(result?.data?.list ?? []).length}
            onClick={() => {
              const downloadUrl = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_V1_VERSION}/micro/inspections/${inspectionId}/download-documents`;
              download(downloadUrl, `${Date.now()}.zip`, {
                Authorization: authContext.token
              });
            }}
          >
            DOWNLOAD ALL ATTACHMENTS
          </Button>
          <Upload
            disabled={isLoading}
            fileList={fileList}
            onChange={(info) => {
              setFileList((fileList || [])?.concat([info.file]));
            }}
            accept={'.png,.jpg,.jpeg'}
            showUploadList={false}
            multiple
            beforeUpload={() => {
              return false;
            }}
          >
            <Button loading={isLoading} type="primary">
              UPLOAD MORE
            </Button>
          </Upload>
        </Space>
      </StyledToolbar>
      {mode === 'list' ? (
        <TableView
          onChange={onChange}
          isLoading={result.isLoading || result.isFetching}
          onDelete={deleteDocument}
          dataSource={result?.data?.list ?? []}
          pagination={{
            ...(result.data as any)?.pagination
          }}
        ></TableView>
      ) : (
        <GridView
          onChange={onChange}
          onDelete={deleteDocument}
          isLoading={result.isLoading || result.isFetching}
          onLoadMore={() => {
            if (result.isLoading || result.isFetching) return;
          }}
          dataSource={result?.data?.list ?? []}
          pagination={{
            ...(result.data as any)?.pagination
          }}
        ></GridView>
      )}
    </Container>
  );
};

export default AttachmentTab;
